import { useTranslation } from "react-i18next";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import { FooterButtons } from "src/common/components/FooterButtons";
import { ModulesSelectedForPublication } from "./ReadOnlyValidationComponents/ModulesSelectedForPublication";
import { PlatformsSelectedForPublication } from "./ReadOnlyValidationComponents/PlatformsSelectedForPublication";
import { ThumbnailSelectedForPublication } from "./ReadOnlyValidationComponents/ThumbnailSelectedForPublication";
import { SourceAppMainMenuMedia360BackgroundSelectedForPublication } from "./ReadOnlyValidationComponents/SourceAppMainMenuMedia360BackgroundSelectedForPublication";
import {
  askPublication,
  getLatestBuildsOfProjectGroup,
  getNewPublicationSettings,
  IAskPublicationPayload,
} from "src/features/builds/state/buildsSlice";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getAllProjectGroupsWithNestedElements } from "src/features/projectGroups/state/projectGroupsSlice";
import { mediaSelector } from "src/features/gallery/state/gallerySlice";
import { useAppDispatch } from "src/common/state/hooks";
import { setAlert } from "src/features/alert/state/alertsSlice";

type Props = {
  incrementStep?(): void;
  decrementStep?(): void;
};

export const CONFIRMATION_BEFORE_PUBLISH_STEP_COMPONENT_ID =
  "confirmationBeforePublishStepComponentId";

export default function ConfirmationBeforePublishStep({ decrementStep }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const newPublicationSettings = useSelector(getNewPublicationSettings);
  const projectGroupId = location.pathname.split("/")[2];
  const projectGroups = useSelector(getAllProjectGroupsWithNestedElements);
  const sourceMedias = useSelector(mediaSelector);
  const selectedPlatforms = useSelector(getNewPublicationSettings).selectedPlatforms;
  const relevantProjectGroup = projectGroups.find((pg) => Number(pg.id) === Number(projectGroupId));
  const relevantPgThumbnail = relevantProjectGroup?.source_thumbnail;
  const default360BackgroundMenuImage = sourceMedias.find((sm) => Number(sm.id) === 5346);
  const latestBuildsOfProjectGroup = useSelector(
    getLatestBuildsOfProjectGroup(relevantProjectGroup.id),
  );
  const latestBuild = latestBuildsOfProjectGroup[0];

  const navigateToPublishView = () => {
    navigate(`/courses/${relevantProjectGroup.id}/publish`);
  };

  const handleSubmit = () => {
    const payload: IAskPublicationPayload = {
      projectGroupId: relevantProjectGroup.id,
      selectedProjectsIds: [],
      selectedPlatforms,
      shouldBeTested: 0,
      isNewVersion: false,
      callback: navigateToPublishView,
    };

    // When republishing same version (!isNewVersion), use exact data from latest build
    // When publishing new version (isNewVersion), use what user selected in SelectModulesStep
    if (!newPublicationSettings.isNewVersion) {
      payload.selectedProjectsIds =
        latestBuild.project_group_snapshot_sorted_by_order.published_projects.map(
          (p) => p.project.id,
        );
      payload.shouldBeTested = latestBuild.should_be_tested ? 1 : 0;
    } else {
      // New version: get the data from the store (user selections)
      payload.selectedProjectsIds = newPublicationSettings.selectedProjectsIds;
      payload.shouldBeTested = newPublicationSettings.shouldBeTested;
      payload.isNewVersion = newPublicationSettings.isNewVersion;
    }

    dispatch(
      askPublication({ ...payload, componentId: CONFIRMATION_BEFORE_PUBLISH_STEP_COMPONENT_ID }),
    )
      .then(() => {
        dispatch(
          setAlert({
            type: "success",
            msg: t("alerts.requestSent"),
            subMsg: t("alerts.publicationSuccess"),
            customTimeoutInMs: 30000,
          }),
        );
        navigateToPublishView();
      })
      .catch(() => {
        dispatch(setAlert({ type: "danger", msg: t("alerts.publicationFail") }));
      });
  };

  return (
    <div className="flex flex-col grow w-full h-0">
      <IsLoading
        componentId={CONFIRMATION_BEFORE_PUBLISH_STEP_COMPONENT_ID}
        showSuccess={false}
        spinnerPlaceholder
      >
        <div className="flex flex-col grow w-full h-0">
          {/* Zone scrollable */}
          <div className="flex flex-col grow overflow-hidden">
            <div
              id="info-section"
              className="w-full gap-1 mt-4 overflow-y-auto px-4"
              style={{ paddingBottom: "1rem" }} // Pour un peu d'espace sous le scroll
            >
              <div className="w-full flex justify-center">
                <div className="w-full max-w-5xl">
                  <ModulesSelectedForPublication
                    selectedProjectIds={
                      !newPublicationSettings.isNewVersion
                        ? latestBuild.project_group_snapshot_sorted_by_order.published_projects.map(
                            (p) => p.project.id,
                          )
                        : newPublicationSettings.selectedProjectsIds
                    }
                  />
                </div>
              </div>

              <div className="w-full flex justify-center mt-4">
                <div className="w-full max-w-5xl">
                  <PlatformsSelectedForPublication
                    selectedPlatforms={newPublicationSettings.selectedPlatforms}
                  />
                </div>
              </div>

              <div className="w-full flex justify-center mt-4">
                <div className="w-full max-w-5xl flex gap-6">
                  <div className="flex-1">
                    <SourceAppMainMenuMedia360BackgroundSelectedForPublication
                      media={relevantProjectGroup.source_app_main_menu_media}
                      defaultMediaUrl={default360BackgroundMenuImage.s3_url}
                    />
                  </div>
                  <div className="flex-1">
                    <ThumbnailSelectedForPublication thumbnailUrl={relevantPgThumbnail.s3_url} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer non scrollable */}
          <div style={{ height: "4.1875rem" }} className="flex-shrink-0">
            <FooterButtons
              cancelText={t("general.cancel")}
              cancelCb={() => navigateToPublishView && navigateToPublishView()}
              backText={t("general.back")}
              backCb={!newPublicationSettings.isNewVersion ? navigateToPublishView : decrementStep}
              nextText={t("general.publish")}
              nextCb={() => handleSubmit()}
            />
          </div>
        </div>
      </IsLoading>
    </div>
  );
}
